import bluebird from 'bluebird'
import {
  CreateReducer,
  CreateAction,
} from '../../store/utils/store'

import {
  IVoidCallback,
  IQuery_Org_Scope,
} from '@kaidam/fc-shared/src/types/utils'

import {
  getProjectHandlers,
  getProjectUrl,
} from '../../store/utils/api'

import {
  IField_Role_Scope,
} from '@kaidam/fc-shared/src/types/schema/role'

import {
  IScope,
} from '@kaidam/fc-shared/src/types/utils'

import {
  IRecord_Astonish_Template,
  IRecord_Astonish_Template_Core,
  IRecord_Astonish_Campaign,
  IRecord_Astonish_Campaign_WithJobs,
  IRecord_Astonish_Campaign_Core,
  IData_Astonish_Send_Test_Payload,
  IData_Astonish_Project_Config,
  IData_Astonish_Campaign_Summary,
  IQuery_Astonish_Campaign_Summary,
  IQuery_Astonish_Campaign_Summary_View_Mode,
  IField_Astonish_Campaign_Mode,
  IData_Astonish_Email_CRM,
} from '@kaidam/fc-shared/src/types/schema/astonish'

import {
  AstonishState,
  ReducerHandler,
  ThunkHandler,
  UpdatePayloadId,
} from '../../types/store'

import {
  NetworkThunk,
} from '../utils/network'

import {
  ASTONISH_CAMPAIGN_STATUS_DRAFT,
  ASTONISH_CAMPAIGN_STATUS_SENDING,
  ASTONISH_CAMPAIGN_MODE_CAMPAIGN,
  ASTONISH_CAMPAIGN_MODE_EMAIL,
  PAGINATION_RESULTS_DEFAULT,
  PAGINATION_ASTONISH_CAMPAIGNS_SORT_DESCRIPTOR,
  ROLE_SCOPE_SELF,
} from '@kaidam/fc-shared/src/constants'

import {
  getSocketClientFactory,
} from '../../store/utils/websocket'

import {
  astonishCampaignChannel,
  astonishTemplateChannel,
} from '@kaidam/fc-shared/src/pubsub/channels'

import {
  IData_Paginated_Query_Wrapper,
  IData_Paginated_Results_Wrapper,
} from '@kaidam/fc-shared/src/types/pagination'

import {
  getPaginatedQuery,
  getPaginationQueryParams,
} from '@kaidam/fc-shared/src/utils/pagination'

import {
  IPubsub_Message_Type_Astonish_Template_Event,
  IPubsub_Message_Body_Astonish_Template_Event,
  IPubsub_Message_Type_Astonish_Campaign_Event,
  IPubsub_Message_Body_Astonish_Campaign_Event,
} from '@kaidam/fc-shared/src/types/pubsub'

import astonishSelectors from '../selectors/astonish'
import projectSelectors from '../selectors/project'
import routerSelectors from '../selectors/router'
import authSelectors from '../selectors/auth'
import snackbarActions from './snackbar'
import routerActions from './router'
import jobActions from './job'

const getTemplateUpdateSocket = getSocketClientFactory<
  IPubsub_Message_Type_Astonish_Template_Event,
  IPubsub_Message_Body_Astonish_Template_Event
>()

const getCampaignUpdateSocket = getSocketClientFactory<
  IPubsub_Message_Type_Astonish_Campaign_Event,
  IPubsub_Message_Body_Astonish_Campaign_Event
>()

const prefix = 'astonish'

/*

  initial state

*/
const initialState: AstonishState = {
  config: {
    from_address: '',
    from_domains: [],
  },
  templates: [],
  campaigns: [],
  campaignsPagination: PAGINATION_RESULTS_DEFAULT,
  createCampaignData: null,
  createCampaignStep: '',
  campaignSummary: [],
  networkSummary: [],
  campaignCRMRecordData: [],
  campaignCRMRecordPagination: PAGINATION_RESULTS_DEFAULT,
  sendFromFranchisees: false,
}

/*

  reducers

*/

const setConfig: ReducerHandler<AstonishState, IData_Astonish_Project_Config> = (state, action) => {
  state.config = action.payload
}

const setTemplates: ReducerHandler<AstonishState, IRecord_Astonish_Template[]> = (state, action) => {
  state.templates = action.payload
}

const setCampaigns: ReducerHandler<AstonishState, IData_Paginated_Results_Wrapper<IRecord_Astonish_Campaign_WithJobs>> = (state, action) => {
  const {
    data,
    pagination,
  } = action.payload
  state.campaigns = data
  state.campaignsPagination = pagination
}

const setCreateCampaignData: ReducerHandler<AstonishState, Partial<IRecord_Astonish_Campaign_Core> | null> = (state, action) => {
  state.createCampaignData = action.payload
}

const mergeCreateCampaignData: ReducerHandler<AstonishState, Partial<IRecord_Astonish_Campaign_Core>> = (state, action) => {
  state.createCampaignData = {
    ...state.createCampaignData,
    ...action.payload,
  }
}

const setCreateCampaignStep: ReducerHandler<AstonishState, string> = (state, action) => {
  state.createCampaignStep = action.payload
}

const setCampaignSummary: ReducerHandler<AstonishState, IData_Astonish_Campaign_Summary[]> = (state, action) => {
  state.campaignSummary = action.payload
}

const setNetworkSummary: ReducerHandler<AstonishState, IRecord_Astonish_Campaign_WithJobs[]> = (state, action) => {
  state.networkSummary = action.payload
}

const setCampaignCRMRecordData: ReducerHandler<AstonishState, IData_Paginated_Results_Wrapper<IData_Astonish_Email_CRM>> = (state, action) => {
  const {
    data,
    pagination,
  } = action.payload
  
  state.campaignCRMRecordData = data
  state.campaignCRMRecordPagination = pagination
}

const setSendFromFranchisees: ReducerHandler<AstonishState, boolean> = (state, action) => {
  state.sendFromFranchisees = action.payload
}

/*

  templates

*/

const loadConfig: ThunkHandler<void, void> = () => async (dispatch, getState) => {
  const handlers = getProjectHandlers(getState)
  const data = await handlers.get<IData_Astonish_Project_Config>(`/astonish/config`)
  dispatch(actions.setConfig(data))
}

const loadTemplates: ThunkHandler<void, void> = () => async (dispatch, getState) => {
  const orgid = projectSelectors.currentOrgId(getState())
  const handlers = getProjectHandlers(getState)
  const data = await handlers.get<IRecord_Astonish_Template[]>(`/astonish/templates/${orgid}`)
  dispatch(actions.setTemplates(data))
}

const createTemplate: ThunkHandler<IRecord_Astonish_Template_Core, boolean> = (data) => async (dispatch, getState) => {
  const orgid = projectSelectors.currentOrgId(getState())
  const handlers = getProjectHandlers(getState)
  const newTemplate = await handlers.post<IRecord_Astonish_Template_Core, IRecord_Astonish_Template>(`/astonish/templates/${orgid}`, data)
  await dispatch(actions.loadTemplates())
  dispatch(snackbarActions.setSuccess('template created'))
  setTimeout(() => {
    dispatch(routerActions.navigateTo('astonish.edit_template', {
      id: newTemplate.id,
    }))
  }, 100)
  return true
}

const updateTemplate: ThunkHandler<UpdatePayloadId<Partial<IRecord_Astonish_Template_Core>>, boolean> = (payload) => async (dispatch, getState) => {
  const orgid = projectSelectors.currentOrgId(getState())
  const handlers = getProjectHandlers(getState)
  await handlers.put<Partial<IRecord_Astonish_Template_Core>, IRecord_Astonish_Template>(`/astonish/templates/${orgid}/${payload.id}`, payload.data)
  await dispatch(actions.loadTemplates())
  dispatch(snackbarActions.setSuccess('template updated'))
  return true
}

const deleteTemplate: ThunkHandler<string, boolean> = (id) => async (dispatch, getState) => {
  const orgid = projectSelectors.currentOrgId(getState())
  const handlers = getProjectHandlers(getState)
  await handlers.delete(`/astonish/templates/${orgid}/${id}`)
  await dispatch(actions.loadTemplates())
  dispatch(snackbarActions.setSuccess('template deleted'))
  return true
}

/*

  campaigns

*/

const loadCampaigns: ThunkHandler<IScope | void, void> = (scope = ROLE_SCOPE_SELF) => async (dispatch, getState) => {
  const route = routerSelectors.route(getState())
  const params = routerSelectors.params(getState())
  const mode = route.name == 'astonish.emails' ?
    ASTONISH_CAMPAIGN_MODE_EMAIL :
    ASTONISH_CAMPAIGN_MODE_CAMPAIGN
  const status = params.campaign_status || 'any'

  const paginatedQuery = getPaginatedQuery(
    params,
    {
      mode,
      status,
    },
    PAGINATION_ASTONISH_CAMPAIGNS_SORT_DESCRIPTOR,
  )

  const paginationQueryParams = getPaginationQueryParams(paginatedQuery.pagination)
  const allQueryParams = Object.assign({}, paginationQueryParams, {
    scope,
    mode,
    status,
  })

  const orgid = projectSelectors.currentOrgId(getState())
  const handlers = getProjectHandlers(getState)
  const data = await handlers.get<IData_Paginated_Results_Wrapper<IRecord_Astonish_Campaign_WithJobs>>(`/astonish/campaigns/${orgid}`, {
    params: allQueryParams
  })
  dispatch(actions.setCampaigns(data))
}

const loadCampaignSummary: ThunkHandler<{
  campaign?: string,
  parent_campaign?: string,
  mode: IQuery_Astonish_Campaign_Summary_View_Mode,
  scope: IField_Role_Scope,
}, void> = ({
  campaign,
  parent_campaign,
  scope,
  mode,
}) => async (dispatch, getState) => {
  const orgid = projectSelectors.currentOrgId(getState())
  const handlers = getProjectHandlers(getState)
  const [
    campaignSummary,
    networkSummary,
  ] = await bluebird.all([
    handlers.put<IQuery_Astonish_Campaign_Summary, IData_Astonish_Campaign_Summary[]>(`/astonish/campaign_summary/${orgid}/${scope}`, {
      mode,
      campaign,
      parent_campaign,
    }),
    handlers.get<IRecord_Astonish_Campaign_WithJobs[]>(`/astonish/campaigns/${orgid}/${parent_campaign || campaign}/network_summary`),
  ])
  dispatch(actions.setCampaignSummary(campaignSummary))
  dispatch(actions.setNetworkSummary(networkSummary))
}

const loadCampaignCRMRecordData: ThunkHandler<IData_Paginated_Query_Wrapper<{
  orgid: string,
  campaignid: string,
  parentmode: boolean,
  filter_org_id: string,
}>, boolean> = (payload) => async (dispatch, getState) => {
  const handlers = getProjectHandlers(getState)
  const {
    pagination,
    query,
  } = payload
  const paginationQueryParams = getPaginationQueryParams(pagination)
  const allQueryParams = Object.assign({}, paginationQueryParams, {
    parentmode: query.parentmode ? '1' : '',
    filter_org_id: query.filter_org_id,
  })
  const data = await handlers.get<IData_Paginated_Results_Wrapper<IData_Astonish_Email_CRM>>(`/astonish/campaigns/${query.orgid}/${query.campaignid}/crm`, {
    params: allQueryParams,
  })
  dispatch(actions.setCampaignCRMRecordData(data))
  return true
}

const openCampaignCRMRecordCSV: ThunkHandler<{
  orgid: string,
  campaignid: string,
  parentmode: boolean,
  filter_org_id: string,
  filename: string,
}, boolean> = ({
  orgid,
  campaignid,
  parentmode,
  filter_org_id,
  filename,
}) => async (dispatch, getState) => {
  const token = authSelectors.token(getState())
  const url = getProjectUrl(getState) + `/astonish/campaigns/${orgid}/${campaignid}/csv?access_token=${token}&parentmode=${parentmode ? '1' : ''}&filter_org_id=${filter_org_id}&filename=${filename}`
  window.open(url)
}

const loadOverallCampaignSummary: ThunkHandler<{
  query: IQuery_Astonish_Campaign_Summary,
  context: IQuery_Org_Scope,
}, void> = (payload) => async (dispatch, getState) => {
  const handlers = getProjectHandlers(getState)
  const data = await handlers.put<IQuery_Astonish_Campaign_Summary, IData_Astonish_Campaign_Summary[]>(`/astonish/campaign_summary/${payload.context.org}/${payload.context.scope}`, payload.query)
  dispatch(actions.setCampaignSummary(data))
}

const createCampaign: ThunkHandler<Partial<IRecord_Astonish_Campaign_Core>, string> = (data) => async (dispatch, getState) => {
  const orgid = projectSelectors.currentOrgId(getState())
  const handlers = getProjectHandlers(getState)
  const postData = Object.assign({}, data, {
    status: ASTONISH_CAMPAIGN_STATUS_DRAFT,
  })
  const created = await handlers.post<Partial<IRecord_Astonish_Campaign_Core>, IRecord_Astonish_Campaign>(`/astonish/campaigns/${orgid}`, postData)
  await dispatch(actions.loadCampaigns())
  return created.id
}

const updateCampaign: ThunkHandler<UpdatePayloadId<Partial<IRecord_Astonish_Campaign_Core>>, boolean> = (payload) => async (dispatch, getState) => {
  const orgid = projectSelectors.currentOrgId(getState())
  const handlers = getProjectHandlers(getState)
  await handlers.put<Partial<IRecord_Astonish_Campaign_Core>, IRecord_Astonish_Campaign>(`/astonish/campaigns/${orgid}/${payload.id}`, payload.data)
  await dispatch(actions.loadCampaigns())
  return true
}

const scheduleCampaign: ThunkHandler<UpdatePayloadId<Partial<IRecord_Astonish_Campaign_Core>>, IRecord_Astonish_Campaign> = ({id, data}) => async (dispatch, getState) => {
  const orgid = projectSelectors.currentOrgId(getState())
  const handlers = getProjectHandlers(getState)
  const saveResult = await dispatch(actions.updateCampaign({
    id,
    data,
  }))
  if(!saveResult) return
  const campaign = await handlers.put<void, IRecord_Astonish_Campaign>(`/astonish/campaigns/${orgid}/${id}/schedule`)
  await dispatch(actions.loadCampaigns())
  dispatch(routerActions.clearQueryParams())
  if(campaign.schedule_job) {
    await dispatch(actions.loadCampaigns())
    dispatch(routerActions.setQueryParams({
      view_job_id: campaign.schedule_job,
    }))
    await dispatch(jobActions.waitForJob(campaign.schedule_job))
    dispatch(routerActions.clearQueryParams())
    dispatch(snackbarActions.setSuccess(`campaign has been scheduled`)) 
  }
  return campaign
}

const cancelCampaign: ThunkHandler<string, boolean> = (id) => async (dispatch, getState) => {
  const orgid = projectSelectors.currentOrgId(getState())
  const handlers = getProjectHandlers(getState)
  await handlers.put<void, IRecord_Astonish_Campaign>(`/astonish/campaigns/${orgid}/${id}/cancel`)
  await dispatch(actions.loadCampaigns())
  dispatch(snackbarActions.setSuccess(`campaign has been cancelled`))
  return true
}

const deleteCampaign: ThunkHandler<string, boolean> = (id) => async (dispatch, getState) => {
  const orgid = projectSelectors.currentOrgId(getState())
  const handlers = getProjectHandlers(getState)
  await handlers.delete(`/astonish/campaigns/${orgid}/${id}`)
  await dispatch(actions.loadCampaigns())
  dispatch(snackbarActions.setSuccess('campaign deleted'))
  return true
}

const sendTest: ThunkHandler<UpdatePayloadId<IData_Astonish_Send_Test_Payload>, boolean> = ({id, data}) => async (dispatch, getState) => {
  const orgid = projectSelectors.currentOrgId(getState())
  const handlers = getProjectHandlers(getState)
  const result = await handlers.post<IData_Astonish_Send_Test_Payload, IRecord_Astonish_Campaign>(`/astonish/campaigns/${orgid}/${id}/test`, data)
  await dispatch(actions.loadCampaigns())
  const editData = astonishSelectors.createCampaignData(getState())
  // merge the server meta back into the campaign edit data
  // so we can see they have sent a test
  dispatch(actions.setCreateCampaignData(Object.assign({}, editData, {
    meta: result.meta,
  })))
  dispatch(snackbarActions.setSuccess(`test email sent to ${data.address}`))
  console.log('Test email result:', result)
  console.log('Test email data:', data)
  return true
}

/*

  websockets

*/
const subscribeToTemplateUpdates: ThunkHandler<void, boolean> = () => async (dispatch, getState) => {
  const {project} = projectSelectors.projectInfo(getState())
  if(!project) return false
  const channel = astonishTemplateChannel.wildcard(project)
  const socket = getTemplateUpdateSocket()
  await socket.subscribe(channel, async (message) => {
    dispatch(loadTemplates())
  })
  return true
}

const unsubscribeToTemplateUpdates: ThunkHandler<void, boolean> = () => async (dispatch, getState) => {
  const {project} = projectSelectors.projectInfo(getState())
  if(!project) return false
  const channel = astonishTemplateChannel.wildcard(project)
  const socket = getTemplateUpdateSocket()
  socket.unsubscribe(channel)
  return true
}

const subscribeToCampaignUpdates: ThunkHandler<IVoidCallback, boolean> = (handler) => async (dispatch, getState) => {
  const {project} = projectSelectors.projectInfo(getState())
  if(!project) return false
  const channel = astonishCampaignChannel.wildcard(project)
  const socket = getCampaignUpdateSocket()
  await socket.subscribe(channel, async (message) => {
    handler()
  })
  return true
}

const unsubscribeToCampaignUpdates: ThunkHandler<void, boolean> = () => async (dispatch, getState) => {
  const {project} = projectSelectors.projectInfo(getState())
  if(!project) return false
  const channel = astonishCampaignChannel.wildcard(project)
  const socket = getCampaignUpdateSocket()
  socket.unsubscribe(channel)
  return true
}

const subscribeToCampaignSummaryUpdates: ThunkHandler<{
  id: string,
  handler: IVoidCallback,
}, boolean> = ({id, handler}) => async (dispatch, getState) => {
  const {project} = projectSelectors.projectInfo(getState())
  if(!project) return false
  const channel = astonishCampaignChannel.get(project, id)
  const socket = getCampaignUpdateSocket()
  await socket.subscribe(channel, async (message) => {
    handler()
  })
  return true
}

const unsubscribeToCampaignSummaryUpdates: ThunkHandler<string, boolean> = (id) => async (dispatch, getState) => {
  const {project} = projectSelectors.projectInfo(getState())
  if(!project) return false
  const channel = astonishCampaignChannel.get(project, id)
  const socket = getCampaignUpdateSocket()
  socket.unsubscribe(channel)
  return true
}

/*

  collections

*/
const reducers = {
  setConfig,
  setTemplates,
  setCampaigns,
  setCreateCampaignData,
  mergeCreateCampaignData,
  setCreateCampaignStep,
  setCampaignSummary,
  setNetworkSummary,
  setCampaignCRMRecordData,
  setSendFromFranchisees,
}

export const actions = {
  setConfig: CreateAction(prefix, `setConfig`, reducers.setConfig),
  setTemplates: CreateAction(prefix, `setTemplates`, reducers.setTemplates),
  setCampaigns: CreateAction(prefix, `setCampaigns`, reducers.setCampaigns),
  setCreateCampaignData: CreateAction(prefix, `setCreateCampaignData`, reducers.setCreateCampaignData),
  mergeCreateCampaignData: CreateAction(prefix, `mergeCreateCampaignData`, reducers.mergeCreateCampaignData),
  setCreateCampaignStep: CreateAction(prefix, `setCreateCampaignStep`, reducers.setCreateCampaignStep),
  setCampaignSummary: CreateAction(prefix, `setCampaignSummary`, reducers.setCampaignSummary),
  setNetworkSummary: CreateAction(prefix, `setNetworkSummary`, reducers.setNetworkSummary),
  setCampaignCRMRecordData: CreateAction(prefix, `setCampaignCRMRecordData`, reducers.setCampaignCRMRecordData),
  setSendFromFranchisees: CreateAction(prefix, `setSendFromFranchisees`, reducers.setSendFromFranchisees),
  loadConfig: NetworkThunk(prefix, 'loadConfig', loadConfig, {
    snackbar: true,
  }),
  loadTemplates: NetworkThunk(prefix, 'loadTemplates', loadTemplates, {
    snackbar: true,
  }),
  createTemplate: NetworkThunk(prefix, 'templateForm', createTemplate, {
    snackbar: true,
    globalLoading: {
      message: 'creating template...'
    },
  }),
  updateTemplate: NetworkThunk(prefix, 'templateForm', updateTemplate, {
    snackbar: true,
    globalLoading: {
      message: 'updating template...'
    },
  }),
  deleteTemplate: NetworkThunk(prefix, 'deleteTemplate', deleteTemplate, {
    snackbar: true,
    globalLoading: {
      message: 'deleting template...'
    },
  }),
  loadCampaigns: NetworkThunk(prefix, 'loadCampaigns', loadCampaigns, {
    snackbar: true,
  }),
  loadCampaignSummary: NetworkThunk(prefix, 'loadCampaignSummary', loadCampaignSummary, {
    snackbar: true,
  }),
  loadCampaignCRMRecordData: NetworkThunk(prefix, 'loadCampaignCRMRecordData', loadCampaignCRMRecordData, {
    snackbar: true,
  }),
  openCampaignCRMRecordCSV: NetworkThunk(prefix, 'openCampaignCRMRecordCSV', openCampaignCRMRecordCSV, {
    snackbar: true,
  }),
  loadOverallCampaignSummary: NetworkThunk(prefix, 'loadOverallCampaignSummary', loadOverallCampaignSummary, {
    snackbar: true,
  }),
  createCampaign: NetworkThunk(prefix, 'campaignForm', createCampaign, {
    snackbar: true,
    globalLoading: {
      message: 'creating campaign...'
    },
  }),
  updateCampaign: NetworkThunk(prefix, 'campaignForm', updateCampaign, {
    snackbar: true,
    globalLoading: {
      message: 'updating campaign...'
    },
  }),
  scheduleCampaign: NetworkThunk(prefix, 'scheduleCampaign', scheduleCampaign, {
    snackbar: true,
    globalLoading: {
      message: 'scheduling campaign...'
    },
  }),
  cancelCampaign: NetworkThunk(prefix, 'cancelCampaign', cancelCampaign, {
    snackbar: true,
    globalLoading: {
      message: 'cancelling campaign...'
    },
  }),
  deleteCampaign: NetworkThunk(prefix, 'deleteCampaign', deleteCampaign, {
    snackbar: true,
    globalLoading: {
      message: 'deleting campaign...'
    },
  }),
  sendTest: NetworkThunk(prefix, 'sendTest', sendTest, {
    snackbar: true,
    globalLoading: {
      message: 'sending test email...'
    },
  }),
  subscribeToTemplateUpdates: NetworkThunk(prefix, 'subscribeToTemplateUpdates', subscribeToTemplateUpdates, {
    snackbar: true,
  }),
  unsubscribeToTemplateUpdates: NetworkThunk(prefix, 'unsubscribeToTemplateUpdates', unsubscribeToTemplateUpdates, {
    snackbar: true,
  }),
  subscribeToCampaignUpdates: NetworkThunk(prefix, 'subscribeToCampaignUpdates', subscribeToCampaignUpdates, {
    snackbar: true,
  }),
  unsubscribeToCampaignUpdates: NetworkThunk(prefix, 'unsubscribeToCampaignUpdates', unsubscribeToCampaignUpdates, {
    snackbar: true,
  }),
  subscribeToCampaignSummaryUpdates: NetworkThunk(prefix, 'subscribeToCampaignSummaryUpdates', subscribeToCampaignSummaryUpdates, {
    snackbar: true,
  }),
  unsubscribeToCampaignSummaryUpdates: NetworkThunk(prefix, 'unsubscribeToCampaignSummaryUpdates', unsubscribeToCampaignSummaryUpdates, {
    snackbar: true,
  }),
}

export const reducer = CreateReducer<AstonishState>({
  prefix,
  initialState,
  reducers,
})

export default actions